<template>
  <div
    :class="{ 'resource-item--solid': isSolid }"
    class="resource-item"
    @click="onResourceClick(true)"
  >
    <img
      v-if="resourceData.image.src"
      :src="resourceData.image.src"
      :alt="resourceData.image.alt"
      class="resource-item__img"
      @click.stop="onResourceClick(false)"
    />

    <div class="resource-item__inner-wrap">
      <span
        v-if="resourceData.title"
        class="resource-item__title text-link"
        @click.stop="onResourceClick(false)"
        v-html="resourceData.title"
      />
      <div
        v-if="resourceData.description"
        class="resource-item__copy"
        :class="{ 'resource-item__copy--truncated': isTextTruncated }"
      >
        <RichRenderer
          v-if="isRichDescription"
          :richTextData="resourceData.description"
          :addWrapClass="true"
        />
        <div v-else class="rich-text-wrapper" v-html="resourceData.description"></div>
      </div>
      <div v-if="resourceData.date || resourceData.author" class="resource-item__bottom-items">
        <span v-if="resourceData.date" class="resource-item__bottom-text text-link">{{
          resourceData.date
        }}</span>
        <span v-if="resourceData.author" class="resource-item__bottom-text text-link">{{
          resourceData.author
        }}</span>
      </div>
    </div>

    <transition name="modal">
      <modal v-if="isModalShow" class="resource-item__modal" @close="closeModal">
        <iframe
          width="600"
          height="335"
          :src="`https://www.youtube.com/embed/${modalData}`"
          frameborder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        />
      </modal>
    </transition>
  </div>
</template>
<script>
import dayjs from '@/lib/utils/dayjs'

import Modal from '@/components/common/Modal.vue'

import { useModal } from '@/composables/useModal'
import { useNavigation } from '@/composables/useNavigation'
import { useSessionStore } from '@/stores/session'

const gatedContentBlacklist = ['Events', 'Press Releases']

export default {
  components: {
    Modal,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    isSolid: {
      type: Boolean,
      default: false,
    },
    isTextTruncated: {
      type: Boolean,
      default: false,
    },
    showAuthor: {
      type: Boolean,
      default: true,
    },
    showDate: {
      type: Boolean,
      default: true,
    },
    isRichDescription: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['loginGatedResourceRequested'],
  setup() {
    const sessionStore = useSessionStore()
    const { navigateTo } = useNavigation()
    const { isModalShow, openModal, closeModal, modalData } = useModal()
    return {
      sessionStore,
      navigateTo,
      isModalShow,
      openModal,
      closeModal,
      modalData,
    }
  },
  computed: {
    resourceData() {
      const descriptionField = this.data.description || this.data.subtitle
      const youtubeVideoUrl = this.getYoutubeVideoUrl(this.data.guestUrl)
      const isYoutubeVideo = !!youtubeVideoUrl
      const loggedInUrl = isYoutubeVideo
        ? youtubeVideoUrl
        : gatedContentBlacklist.includes(this.data.resourceGroup)
          ? this.data.link
          : this.data.loggedInUrl
      return {
        isLoginGated: !this.sessionStore.isUserLoggedIn && this.data.isLoginGated,
        isSelfOpenTarget: this.data.isSelfOpenTarget,
        isYoutubeVideo,
        image: {
          src: isYoutubeVideo
            ? `https://img.youtube.com/vi/${youtubeVideoUrl}/0.jpg`
            : this.data.img || null,
          alt: this.data.imgAlt || this.data.title,
        },
        loggedInUrl,
        guestUrl: isYoutubeVideo ? youtubeVideoUrl : this.data.guestUrl,
        title: this.isTextTruncated ? this.$truncate(this.data.title, 70) : this.data.title,
        description: descriptionField,
        date: this.showDate && this.data.date ? this.formatDate(this.data.date) : null,
        author: this.showAuthor && this.data.author ? this.data.author : null,
      }
    },
  },
  methods: {
    getYoutubeVideoUrl(url) {
      if (url && url.includes('youtu')) {
        // eslint-disable-next-line
        const regExp =
          /^.*(?:(?:youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/)|(?:(?:watch)?\?v(?:i)?=|&v(?:i)?=))([^#&?]+).*/
        const match = url.trim().match(regExp)
        return match && match[1] ? match[1] : null
      } else {
        return null
      }
    },
    onResourceClick(isRootElement) {
      if (!this.isSolid && isRootElement) return

      if (this.resourceData.isYoutubeVideo) {
        this.openModal(this.resourceData.loggedInUrl)
        return
      }

      if (this.resourceData.isLoginGated) {
        this.$emit('loginGatedResourceRequested', {
          loggedInUrl: this.resourceData.loggedInUrl,
          guestUrl: this.resourceData.guestUrl,
        })
        return
      }

      const target = this.resourceData.isSelfOpenTarget ? '_self' : '_blank'
      this.navigateTo(this.resourceData.loggedInUrl, target)
    },
    formatDate(date) {
      return dayjs(date).format('MMMM D, YYYY')
    },
  },
}
</script>
